import React, { useState, useEffect } from 'react';
import { estimateTokenUsage } from '../../services/modelService';
import { Spinner } from '../common/Spinner';

interface CostEstimatorProps {
  modelId: string;
  inputText?: string;
  messages?: any[];
  estimatedOutputLength?: number;
  darkMode?: boolean;
  className?: string;
}

export const CostEstimator: React.FC<CostEstimatorProps> = ({
  modelId,
  inputText,
  messages,
  estimatedOutputLength,
  darkMode = false,
  className = ''
}) => {
  const [estimate, setEstimate] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchEstimate = async () => {
      // Only proceed if we have a model ID AND either inputText or messages
      if (!modelId || (!inputText && (!messages || messages.length === 0))) {
        console.log("Skipping token estimation due to missing required data");
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const result = await estimateTokenUsage(
          modelId,
          inputText,
          messages,
          estimatedOutputLength
        );
        setEstimate(result);
      } catch (err: any) {
        console.error("Error estimating token usage:", err);
        setError(err.message || 'Failed to estimate token usage');
        // Set a fallback estimate with default values
        setEstimate({
          input_tokens: inputText ? Math.ceil(inputText.length / 4) : (messages ? 100 : 0),
          estimated_output_tokens: estimatedOutputLength || 100,
          total_tokens: (inputText ? Math.ceil(inputText.length / 4) : (messages ? 100 : 0)) + (estimatedOutputLength || 100),
          context_length: 8192,
          context_percentage: 5,
          prompt_cost_millicents: 0,
          estimated_completion_cost_millicents: 0,
          total_cost_millicents: 0,
          formatted_total_cost: "$0.00 (estimate unavailable)"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchEstimate();
  }, [modelId, inputText, messages, estimatedOutputLength]);
  
  if (loading) return <div className="flex justify-center p-4"><Spinner size="md" /></div>;
  if (error && !estimate) return <div className="text-red-500 p-4">Error: {error}</div>;
  if (!estimate) return <div className="p-4 text-gray-500">Waiting for input data to estimate costs...</div>;
  
  // Determine context usage color
  const getContextColor = (percentage: number) => {
    if (percentage > 90) return darkMode ? 'text-red-400' : 'text-red-600';
    if (percentage > 70) return darkMode ? 'text-yellow-400' : 'text-yellow-600';
    return darkMode ? 'text-green-400' : 'text-green-600';
  };
  
  return (
    <div className={`${className} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <h3 className={`text-lg font-medium mb-3`}>
        Cost Estimate
      </h3>
      
      <div className="grid grid-cols-2 gap-3">
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Input Tokens:</div>
        <div className="font-medium">{estimate.input_tokens.toLocaleString()}</div>
        
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Est. Output Tokens:</div>
        <div className="font-medium">{estimate.estimated_output_tokens.toLocaleString()}</div>
        
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Total Tokens:</div>
        <div className="font-medium">{estimate.total_tokens.toLocaleString()}</div>
        
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Context Usage:</div>
        <div className={`font-medium ${getContextColor(estimate.context_percentage)}`}>
          {estimate.context_percentage.toFixed(1)}% of {estimate.context_length.toLocaleString()}
        </div>
        
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Input Cost:</div>
        <div className="font-medium">${(estimate.prompt_cost_millicents / 100000).toFixed(4)}</div>
        
        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Est. Output Cost:</div>
        <div className="font-medium">${(estimate.estimated_completion_cost_millicents / 100000).toFixed(4)}</div>
        
        <div className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>Total Cost:</div>
        <div className="text-lg font-bold">{estimate.formatted_total_cost}</div>
      </div>
    </div>
  );
};
